<template>
  <div class="content-wrapper">
    <app-page-top-section :title="$t('report')">
      <div class="mb-3">
        <select v-model="selectedProject" @change="loadProjectReport" class="form-select d-inline-block" style="width: 300px; margin-right: 10px;">
          <option value="">All Projects</option>
          <option v-for="project in projects" :key="project.id" :value="project.id">
            {{ project.title }}
          </option>
        </select>
        <button class="btn btn-primary" @click="generatePdf">
          <i class="fa fa-download"></i> {{ $t('generate_pdf') }}
        </button>
      </div>
    </app-page-top-section>

    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ $t('total_projects') }}</h5>
            <h2 class="text-primary">{{ statistics.total_projects || 0 }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ $t('total_clients') }}</h5>
            <h2 class="text-success">{{ statistics.total_clients || 0 }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ $t('total_income') }}</h5>
            <h2 class="text-info">{{ formatCurrency(statistics.total_income) }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ $t('total_project_payments') }}</h5>
            <h2 class="text-warning">{{ formatCurrency(statistics.total_project_payments) }}</h2>
          </div>
        </div>
      </div>
    </div>

    <!-- Project & Client-wise Report Table -->
    <div class="card">
      <div class="card-header">
        <h4>{{ $t('project_client_wise_report') }}</h4>
      </div>
      <div class="card-body">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>{{ $t('project') }}</th>
              <th>{{ $t('client') }}</th>
              <th>{{ $t('total_payments') }}</th>
              <th>{{ $t('total_income') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(report, index) in projectReports" :key="index">
              <td>{{ report.project_title }}</td>
              <td>{{ report.client_name || '-' }}</td>
              <td>{{ formatCurrency(report.total_payments || 0) }}</td>
              <td>{{ formatCurrency(report.total_income || 0) }}</td>
              <td>
                <button class="btn btn-sm btn-primary" @click="viewProjectClientDetail(report)">{{ $t('view_details') }}</button>
                <button class="btn btn-sm btn-success" @click="generatePdfForProjectClient(report)">{{ $t('generate_pdf') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Project & Client Detail Modal -->
    <div class="modal fade" id="projectDetailModal" tabindex="-1" v-if="selectedProjectDetail">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedProjectDetail.project_title }} {{ selectedProjectDetail.client_name ? '- ' + selectedProjectDetail.client_name : '' }} - {{ $t('details') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>{{ $t('project') }}:</strong> {{ selectedProjectDetail.project_title }}
              </div>
              <div class="col-md-6">
                <strong>{{ $t('client') }}:</strong> {{ selectedProjectDetail.client_name || '-' }}
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-12">
                <strong>{{ $t('details') }}:</strong> {{ selectedProjectDetail.project_details || '-' }}
              </div>
            </div>
            
            <h6>{{ $t('payments') }}</h6>
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ $t('date') }}</th>
                  <th>{{ $t('amount') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in selectedProjectDetail.payments" :key="payment.id">
                  <td>{{ formatDate(payment.date) }}</td>
                  <td>{{ formatCurrency(payment.payment_amount) }}</td>
                </tr>
                <tr v-if="!selectedProjectDetail.payments || selectedProjectDetail.payments.length === 0">
                  <td colspan="2">No payments found</td>
                </tr>
              </tbody>
            </table>

            <h6>{{ $t('income') }}</h6>
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ $t('date') }}</th>
                  <th>{{ $t('title') }}</th>
                  <th>{{ $t('amount') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="income in selectedProjectDetail.incomes" :key="income.id">
                  <td>{{ formatDate(income.date) }}</td>
                  <td>{{ income.title }}</td>
                  <td>{{ formatCurrency(income.amount) }}</td>
                </tr>
                <tr v-if="!selectedProjectDetail.incomes || selectedProjectDetail.incomes.length === 0">
                  <td colspan="3">No income found</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppReports',
  data() {
    return {
      statistics: {},
      projects: [],
      projectReports: [],
      selectedProject: '',
      selectedProjectDetail: null
    }
  },
  mounted() {
    this.fetchStatistics()
    this.fetchProjects()
    this.loadProjectReport()
  },
  methods: {
    fetchStatistics() {
      axios.get('/reports/statistics')
        .then(res => {
          this.statistics = res.data
          this.projectReports = res.data.projects || []
        })
        .catch(err => console.error(err))
    },
    fetchProjects() {
      axios.get('/project-payments/projects')
        .then(res => { this.projects = res.data })
        .catch(err => console.error(err))
    },
    loadProjectReport() {
      const url = this.selectedProject 
        ? `/reports/project-report/${this.selectedProject}`
        : '/reports/project-report'
      
      axios.get(url)
        .then(res => {
          this.projectReports = res.data
        })
        .catch(err => console.error(err))
    },
    viewProjectClientDetail(report) {
      this.selectedProjectDetail = report
      // Show modal using jQuery/Bootstrap
      $('#projectDetailModal').modal('show')
    },
    generatePdf() {
      const url = this.selectedProject
        ? `/reports/generate-pdf/${this.selectedProject}`
        : '/reports/generate-pdf'
      window.open(url, '_blank')
    },
    generatePdfForProjectClient(report) {
      // Generate PDF for specific project-client combination
      const url = report.client_id 
        ? `/reports/generate-pdf/${report.project_id}?client_id=${report.client_id}`
        : `/reports/generate-pdf/${report.project_id}`
      window.open(url, '_blank')
    },
    formatCurrency(amount) {
      if (!amount) return '0.00'
      return parseFloat(amount).toFixed(2)
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    }
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 20px;
}
.card-body h2 {
  margin: 0;
  font-size: 2rem;
}
</style>

